import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import clsx from 'clsx';
import { toHashColorHexString } from '../utils/colorUtils';
import { FlamingoFund, UnitCategoryCode } from 'funds-model';
import { Currency } from '../Currency/Currency';
import Decimal from 'decimal.js';
import { ROI } from './fundListOptions';
import FundListItemIcons from './FundListItemIcons';
import { getChooseButtonAriaLabel, getPeriodText } from './tools/FundList.tools';
import {Title } from '../Title/Title';
import {Card} from '../Card/Card';
import {Button} from '../Button/Button';
import {Typography} from '../Typography/Typography';
import {RiskProgressBar} from '../RiskProgressBar/RiskProgressBar';
import { useIsMobile } from '../utils/useIsMobile';
import { LabelWithBar } from '../LabelWithBar/LabelWithBar';
import { LabelWithTooltip } from '../LabelWithTooltip/LabelWithTooltip';
import { Divider } from '../Divider/Divider';
import MultipleWordsTextWithArrow from '../MultipleWordsTextWithArrow/MultipleWordsTextWithArrow';
import { ReactComponent as CheckmarkIcon } from 'common-assets/assets/icons/checkmark.svg';

import useStyles from './FundListItem.style';
export interface FundListItemProps {
  hasMobilePricingDate?: boolean;
  fund: FlamingoFund;
  fundValue?: Decimal;
  isFundInadequate: boolean;
  roi: ROI;
  unitCategoryCode: UnitCategoryCode;
  onSelectFund?: (fund: FlamingoFund, setIsButtonLoading?: (value: boolean) => void) => void;
  isSelected?: boolean;
  isButtonDisabled?: boolean;
  dynamicUnitCategoryCodeInDetails?: boolean;
  fundDetailsDialogProcessLayout?: boolean;
  className?: string;
  index: number;
  autoTestId?: string;

  renderFundChart: (
    fund: FlamingoFund,
    unitCategoryCode: UnitCategoryCode,
    rateKey: ROI,
    options: {
      isMobile?: boolean;
      fundBoxClassName?: string;
    }
  ) => React.ReactNode;

  renderFundDetailsDialog?: (
    fund: FlamingoFund,
    unitCategoryCode: UnitCategoryCode,
    index: number,
    options: {
      isMobile?: boolean;
      isSelected?: boolean;
      isButtonDisabled?: boolean;
      isFundInadequate?: boolean;
      roi: ROI;
      fundDetailsDialogProcessLayout?: boolean;
      dynamicUnitCategoryCodeInDetails?: boolean;
      onSelectFund?: () => void;
      className?: string;
      mobileRateKey?: ROI;
      customButton?: React.ReactNode;
      titleAsButton?: boolean;
    }
  ) => React.ReactNode;
}

const FundListItem: React.FC<FundListItemProps> = ({
  fund,
  fundValue,
  roi,
  unitCategoryCode,
  onSelectFund,
  hasMobilePricingDate,
  className,
  isFundInadequate,
  isSelected = false,
  isButtonDisabled = false,
  dynamicUnitCategoryCodeInDetails = false,
  fundDetailsDialogProcessLayout = false,
  renderFundChart,
  renderFundDetailsDialog,
  index,
  autoTestId,
}) => {
  const c = useStyles({ isSelected});
  const [t] = useTranslation('components');
  const isMobile = useIsMobile( 'sm');
  const quotesForFund = fund.quotesForUnitCode[unitCategoryCode];
  const quoteDisplayDate =
    quotesForFund?.date != null && DateTime.fromISO(quotesForFund.date).isValid
      ? DateTime.fromISO(quotesForFund.date).toFormat('dd.MM')
      : undefined;
  const pricingLabelText = quoteDisplayDate
    ? `${t('fund-list.pricing')} ${quoteDisplayDate}`
    : t('fund-list.pricing');
  const [isButtonLoading, setIsButtonLoading] = useState(false);

  const chartBox = useMemo(() => {
    return renderFundChart(fund, unitCategoryCode, roi, {
      isMobile,
      fundBoxClassName: onSelectFund ? c.fundBoxPurchase : c.fundBox,
    });
  }, [
    renderFundChart,
    fund,
    unitCategoryCode,
    isMobile,
    roi,
    onSelectFund,
    c.fundBoxPurchase,
    c.fundBox,
  ]);

  const periodText = getPeriodText(roi, t);

  const fundName = fund.name;
  const chooseButtonAriaLabel = getChooseButtonAriaLabel(fundName, t, isSelected);
  const titleNode = (
    <MultipleWordsTextWithArrow
      text={fund?.name}
      classNameWrapper={c.textWrapper}
      classNameText={c.text}
      classNameIcon={c.icon}
    />
  );

  return (
    <Card size="equal" className={clsx(c.root, className)} hoverable autoTestId={autoTestId}>
      <div className={c.titleBox}>
        <div className={c.titleWithChevronBox}>
          <FundListItemIcons
            fund={fund}
            fundValue={fundValue}
            isFundInadequate={isFundInadequate}
            umbrellaColor={toHashColorHexString(fund?.umbrella?.colorHex)}
          />
            {renderFundDetailsDialog?.(fund, unitCategoryCode, index, {
              isMobile,
              isSelected,
              isButtonDisabled,
              isFundInadequate,
              roi,
              fundDetailsDialogProcessLayout,
              dynamicUnitCategoryCodeInDetails,
              onSelectFund: onSelectFund ? () => onSelectFund(fund, setIsButtonLoading) : undefined,
              className: c.button,
              customButton: <Title
                              title={titleNode}
                              titleComponent="h4"
                              titleVariant='h4'
                              titleFontWeight="bold"
                            />,
              titleAsButton: true
            })}
            {fund.previousName && <Typography variant="body2" textColor="greys.60" className={c.oldFundName}>{fund.previousName}</Typography>}
        </div>
        {!!onSelectFund &&
          <div className={c.actionBox}>
            <div className={c.buttonBox}>
              <Button
                key={`${fund.id}${isSelected}`}
                disabled={isButtonDisabled}
                isLoading={isButtonLoading}
                buttonType={isSelected ? 'primary' : 'secondary'}
                onClick={() => onSelectFund(fund, setIsButtonLoading)}
                aria-label={chooseButtonAriaLabel}
                role="switch"
                aria-checked={isSelected}
                buttonVariant={isMobile ? 'small' : 'medium'}
                customIcon={isSelected ? <CheckmarkIcon className={c.checkmarkIcon} /> : null}
                iconPosition='before'
              >
                {isSelected ? t('fund-list.chosen') :t('fund-list.choose')}
              </Button>
            </div>
          </div>
        }
        {isMobile && <Divider fullWidth={true} size="small" />}
      </div>
      <div className={c.infoWrapper}>
        <div className={c.infoBox}>
          <LabelWithTooltip
            labelText={t('fund-list.fundType')}
            preTitle={fund.fundType.tooltipTitle}
            title={fund.fundType.description}
            size="s"
            color="graphite"
          />
          <LabelWithBar text={fund.fundType.name} color={toHashColorHexString(fund?.fundType?.colorHex)} />
        </div>
        <div className={c.infoBox}>
          <LabelWithTooltip
            labelText={`${t('fund-list.risk')}: ${fund.risk.filterLabel}`}
            title={t('fund-details-dialog.risk-tooltip')}
            size="s"
            color="graphite"
          />
          <RiskProgressBar
            riskValue={fund.risk.value}
            withRiskValueLabel
          />
        </div>
        {isMobile && <Divider fullWidth={true} size="small" />}
        <div className={c.infoBox}>
          <LabelWithTooltip
            labelText={t('fund-list.horizon')}
            title={t('fund-details-dialog.horizon-tooltip')}
            size="s"
            color="graphite"
          />
          <span className={'c.horizon'}>
            {t(`fund-list.options.horizon.${fund.horizonValue}-years`)}
          </span>
        </div>
        <div className={c.infoBox}>
          <LabelWithTooltip
            labelText={pricingLabelText}
            title={t('fund-details-dialog.pricing-tooltip')}
            size="s"
            color="graphite"
          />
          {quotesForFund
            ? <Currency
                valueFontWeight="bold"
                valueClassName={c.currency}
                value={new Decimal(quotesForFund.value).toNumber()} />
            : <span>{t('fund-list.no-pricing')}</span>
          }
        </div>
        {isMobile && <Divider fullWidth={true} size="small" />}

        <div className={c.infoBox}>
          <LabelWithTooltip
            labelText={`${t('fund-list.roi')}: ${roi}`}
            title={t('fund-list.roi-tooltip', { period: periodText })}
            size="s"
            color="graphite"
          />
          {chartBox}
        </div>

      </div>
    </Card>
  );
};

export default FundListItem;
